/**
 * You can override default metatags set in this component in two ways:
 * 1. Send in content as props
 * 2. Send in the whole tag as a child node with the correct `key=`
 *
 * The Open Graph properties for title and description have an empty
 * string in Default props in order to be defaulted to the `title` and `description`.
 */
import Head from 'next/head';
import { withRouter } from 'next/router';
import React, { FunctionComponent } from 'react';

type Props = {
  title?;
  description?;
  ogDescription?;
  ogImage?;
  ogSiteName?;
  ogTitle?;
  ogVideo?;
  ogVideoSecureUrl?;
  ogType?;
  ogVideoType?;
  ogVideoWidth?;
  ogVideoHeight?;
  children: JSX.Element;
  router;
};

const DynamicHead: FunctionComponent<Props> = ({
  title = 'subaru.no',
  description = '',
  ogDescription = '', // Leave this empty to use `description` as fallback
  ogImage = '',
  ogSiteName = 'subaru.no',
  ogTitle = '', // Leave this empty to use `title` as fallback
  ogVideo = '',
  ogVideoSecureUrl = '',
  ogType = 'website',
  ogVideoType = '',
  ogVideoWidth = '',
  ogVideoHeight = '',
  children = <></>,
}) => {
  return (
    <Head>
      <title key="title">{title}</title>

      <meta key="description" name="description" content={description} />
      <meta key="og:description" property="og:description" content={ogDescription || description} />
      <meta key="og:type" property="og:type" content={ogType || 'website'} />
      <meta key="og:image" property="og:image" content={ogImage} />

      <meta key="og:site_name" property="og:site_name" content={ogSiteName} />
      <meta key="og:title" property="og:title" content={ogTitle || title} />
      {ogVideo && ogVideoSecureUrl && (
        <>
          <meta key="og:video" property="og:video" content={ogVideo} />
          <meta
            key="og:video:secure_url"
            property="og:video:secure_url"
            content={ogVideoSecureUrl}
          />
          <meta key="og:video:type" property="og:video:type" content={ogVideoType} />
          <meta key="og:video:width" property="og:video:width" content={ogVideoWidth} />
          <meta key="og:video:height" property="og:video:height" content={ogVideoHeight} />
        </>
      )}
      {children}
    </Head>
  );
};

export default withRouter(DynamicHead);
